import { UploadCloud, HelpCircle, ChevronsUpDown, LogOut, Check, ShieldCheck, User as UserIcon, Palette } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export default function Topbar() {
  const { user, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const onSwitch = async (role) => {
    if (role === user.role) return;
    try {
      await switchRole(role);
      toast.success(`Switched to ${role === "admin" ? "Admin" : "Customer"} view`);
    } catch (e) {
      toast.error("Could not switch role");
    }
  };

  const initials = (user.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="h-16 sticky top-0 z-20 bg-[#0E131F]/90 backdrop-blur-md border-b border-gray-800/80 px-6 flex items-center justify-between">
      {/* Account / View switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="account-switcher"
            className="flex items-center gap-3 rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-1.5 hover:border-amber-500/40 transition-colors"
          >
            <img src="/streamworship-mark.png" alt="StreamWorship" className="h-6 w-6 object-contain" />
            <div className="text-left leading-tight">
              <p className="text-sm font-medium text-white">StreamWorship</p>
              <p className="text-[10px] text-gray-500">{user.role === "admin" ? "Admin view" : "Customer view"}</p>
            </div>
            <ChevronsUpDown className="h-4 w-4 text-gray-500 ml-1" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 bg-card border-gray-800 text-gray-200">
          <DropdownMenuLabel className="text-[10px] tracking-widest text-gray-500">SWITCH VIEW</DropdownMenuLabel>
          <DropdownMenuItem data-testid="account-switch-customer" onClick={() => onSwitch("customer")} className="cursor-pointer focus:bg-gray-800">
            <UserIcon className="h-4 w-4 mr-2 text-gray-400" /> Customer
            {user.role === "customer" && <Check className="h-4 w-4 ml-auto text-amber-400" />}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="account-switch-admin" onClick={() => onSwitch("admin")} className="cursor-pointer focus:bg-gray-800">
            <ShieldCheck className="h-4 w-4 mr-2 text-gray-400" /> Admin
            {user.role === "admin" && <Check className="h-4 w-4 ml-auto text-amber-400" />}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex items-center gap-2">
        <span
          data-testid="role-badge"
          className={
            "hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border " +
            (user.role === "admin"
              ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
              : "bg-gray-500/15 text-gray-300 border-gray-500/30")
          }
        >
          {user.role === "admin" ? <ShieldCheck className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
          {user.role === "admin" ? "Admin" : "Customer"}
        </span>

        <button data-testid="upload-button" className="h-9 w-9 rounded-lg text-gray-400 hover:text-amber-400 hover:bg-gray-800/60 flex items-center justify-center transition-colors">
          <UploadCloud className="h-[18px] w-[18px]" />
        </button>
        <button data-testid="help-button" className="h-9 w-9 rounded-lg text-gray-400 hover:text-amber-400 hover:bg-gray-800/60 flex items-center justify-center transition-colors">
          <HelpCircle className="h-[18px] w-[18px]" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-button" className="flex items-center gap-2 rounded-lg pl-1 pr-2 py-1 hover:bg-gray-800/60 transition-colors">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover border border-amber-500/30" />
              ) : (
                <div className="h-8 w-8 rounded-full gold-gradient flex items-center justify-center text-xs font-bold text-black">{initials}</div>
              )}
              <span className="hidden sm:block text-sm font-medium text-white max-w-[120px] truncate">{user.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 bg-card border-gray-800 text-gray-200">
            <DropdownMenuLabel className="flex flex-col">
              <span className="text-sm font-semibold text-white">{user.name}</span>
              <span className="text-xs text-gray-500 font-normal truncate">{user.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-gray-800" />
            <DropdownMenuLabel className="text-[10px] tracking-widest text-gray-500">SWITCH VIEW</DropdownMenuLabel>
            <DropdownMenuItem data-testid="role-switch-customer" onClick={() => onSwitch("customer")} className="cursor-pointer focus:bg-gray-800">
              <UserIcon className="h-4 w-4 mr-2 text-gray-400" /> Customer
              {user.role === "customer" && <Check className="h-4 w-4 ml-auto text-amber-400" />}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="role-switch-admin" onClick={() => onSwitch("admin")} className="cursor-pointer focus:bg-gray-800">
              <ShieldCheck className="h-4 w-4 mr-2 text-gray-400" /> Admin
              {user.role === "admin" && <Check className="h-4 w-4 ml-auto text-amber-400" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-800" />
            <DropdownMenuItem data-testid="branding-menu-link" onClick={() => navigate("/branding")} className="cursor-pointer focus:bg-gray-800">
              <Palette className="h-4 w-4 mr-2 text-gray-400" /> Organization Branding
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-800" />
            <DropdownMenuItem data-testid="logout-button" onClick={logout} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-400">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
